'use client'

import Script from 'next/script'
import { useCallback, useEffect, useRef, useState } from 'react'

import { Button } from '@/app/components/atoms/Button'
import { IconChevLeft, IconChevRight, IconPause, IconPlay } from '@/app/components/atoms/Icons'
import { useOptionalPageAnalytics } from '@/app/context/analytics'
import { NO_APLICA } from '@/app/lib/analytics/constants'
import { slugify } from '@/app/lib/analytics/dataLayer'
import type { ViewPromotionParams } from '@/app/lib/analytics/types'
import { useYouTubeIframeApi } from '@/app/lib/hooks/useYouTubeIframeApi'

import {
  CAROUSEL_ANALYTICS_MODULE_NAME,
  CAROUSEL_ANALYTICS_MODULE_POSITION,
  CAROUSEL_ANALYTICS_MODULE_TYPE,
  CAROUSEL_CREATIVE_NAME,
  CAROUSEL_CREATIVE_SLOT,
  CTA_LABEL,
  HIGHLIGHTED_LABEL,
  PRICE_FROM_PREFIX,
  SECTION_LABEL,
} from './constants'
import styles from './HeroCarousel.module.scss'
import { useHeroCarouselPlayback } from './hooks/useHeroCarouselPlayback'
import { useRailSwipe } from './hooks/useRailSwipe'
import type { HeroCarouselClientProps, HeroCarouselItem } from './types'

/** Cuántas carátulas se muestran en el rail (la activa + las siguientes). */
const RAIL_SIZE = 4

/** Umbral que exige el diccionario para las impresiones (`view_promotion`). */
const IMPRESSION_THRESHOLD = 0.5

/** `view_promotion`/`select_promotion` de una slide (`Carousel.csv`): mismos
 * campos en ambos eventos (guía `02_Snippets&DataLayer.md` §5), solo cambia
 * `index` (1-based, posición de la slide en `items`). */
function toPromotionParams(item: HeroCarouselItem, index: number): ViewPromotionParams {
  return {
    module_name: CAROUSEL_ANALYTICS_MODULE_NAME,
    module_type: CAROUSEL_ANALYTICS_MODULE_TYPE,
    module_position: CAROUSEL_ANALYTICS_MODULE_POSITION,
    promotion_id: item.id,
    promotion_name: slugify(item.title || item.link || item.id),
    creative_name: CAROUSEL_CREATIVE_NAME,
    creative_slot: CAROUSEL_CREATIVE_SLOT,
    index: index + 1,
  }
}

function formatPrice(amount: number): string {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
  }).format(amount)
}

/**
 * Interactividad del HeroCarousel: fondo panorámico con Ken Burns o vídeo de
 * YouTube (precargado en segundo plano), avance automático, copy, CTA y rail
 * de carátulas de próximos eventos. Recibe los items ya
 * normalizados/ordenados/validados — no conoce `type_content` ni ninguna
 * forma cruda del backend (FR-003).
 */
export function HeroCarouselClient({ items }: HeroCarouselClientProps) {
  const hasAnyVideo = items.some((item) => item.videoId !== null)
  const { ready: apiReady, shouldRenderScript } = useYouTubeIframeApi(hasAnyVideo)
  const sectionRef = useRef<HTMLElement | null>(null)

  // Fuera del viewport el carrusel se detiene (avance y vídeo) sin marcarlo
  // como pausado por el usuario — al volver a la vista se reanuda solo.
  // Arranca en `true`: el hero es la primera pieza de la página y así el
  // primer vídeo no espera a que resuelva el observer.
  // (El observer se registra más abajo, tras el de impresiones de analítica.)
  const [isInView, setIsInView] = useState(true)

  const {
    activeIndex,
    isVideoPlaying,
    visibleSlot,
    slotARef,
    slotBRef,
    goTo,
    isPaused,
    togglePause,
    hasVideoSlide,
  } = useHeroCarouselPlayback({ items, apiReady, isInView })

  // Arrastre con ratón sobre el rail de carátulas → cambia de slide (el rail
  // no es desplazable, ver `useRailSwipe`).
  const railRef = useRef<HTMLDivElement | null>(null)
  useRailSwipe(railRef, (steps) => goTo(activeIndex + steps))

  // La slide 0 está activa desde el primer render (`activeIndex` arranca en
  // 0) — sin este retraso de un tick, su imagen nunca pasaría por un estado
  // "inactiva" pintado antes del "activa", y el Ken Burns (una transición
  // CSS, no una animación) no tendría un valor de partida del que animar.
  // Las demás slides no necesitan esto: sí pasan por ese ciclo real al
  // activarse durante el avance del carrusel.
  const [mounted, setMounted] = useState(false)
  useEffect(() => {
    // `requestAnimationFrame` (no un `setState` síncrono en el cuerpo del
    // efecto) para garantizar que el navegador ya pintó el frame "inactivo"
    // antes de pasar a "activo" — si no, no hay transición CSS que animar.
    const raf = requestAnimationFrame(() => setMounted(true))
    return () => cancelAnimationFrame(raf)
  }, [])

  // ── Analítica (`Carousel.csv`, módulo transversal) ──────────────────────
  // `view_promotion`: una vez por slide, la primera vez que el carrusel cruza
  // el 50% de viewport Y esa slide pasa a ser la activa — no se dispara si el
  // carrusel está fuera de pantalla (IntersectionObserver, threshold 0.5,
  // guía `02_Snippets&DataLayer.md` §9). Tolerante fuera de
  // `PageAnalyticsProvider` (`useOptionalPageAnalytics`): el carrusel también
  // se monta en páginas de categoría aún sin instrumentar.
  const analytics = useOptionalPageAnalytics()
  const isVisibleRef = useRef(false)
  const trackedPromotionIdsRef = useRef<Set<string>>(new Set())

  const trackActiveSlideImpression = useCallback(() => {
    if (!isVisibleRef.current) return
    const item = items[activeIndex]
    if (!item || trackedPromotionIdsRef.current.has(item.id)) return
    trackedPromotionIdsRef.current.add(item.id)
    analytics?.trackViewPromotion(toPromotionParams(item, activeIndex))
  }, [analytics, items, activeIndex])

  // El callback se guarda en un ref (actualizado en un efecto, nunca durante
  // el render) para que el observer de abajo — que solo se crea UNA vez, al
  // montar — pueda invocar siempre la versión más reciente (misma técnica que
  // `TrackedProductCard`).
  const trackActiveSlideImpressionRef = useRef(trackActiveSlideImpression)
  useEffect(() => {
    trackActiveSlideImpressionRef.current = trackActiveSlideImpression
  })

  useEffect(() => {
    const node = sectionRef.current
    if (!node) return undefined

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return
        isVisibleRef.current = true
        // El carrusel puede cruzar el 50% de viewport DESPUÉS del montaje
        // (p. ej. si en algún futuro contexto no fuera la primera pieza de la
        // página) — hay que disparar la impresión aquí mismo, no solo
        // confiar en el efecto de más abajo (que ya pudo haber corrido antes
        // de que el observer resolviera).
        trackActiveSlideImpressionRef.current()
        observer.disconnect()
      },
      { threshold: IMPRESSION_THRESHOLD },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    trackActiveSlideImpression()
  }, [trackActiveSlideImpression])

  // Visibilidad para detener el carrusel (ver `isInView`, arriba).
  useEffect(() => {
    const node = sectionRef.current
    if (!node) return undefined
    const observer = new IntersectionObserver((entries) => {
      const entry = entries[entries.length - 1]
      if (entry) setIsInView(entry.isIntersecting)
    })
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  const activeItem = items[activeIndex]
  if (!activeItem) return null

  const priceText = activeItem.minPrice != null ? formatPrice(activeItem.minPrice) : null
  const railCount = Math.min(RAIL_SIZE, items.length)

  function handleSelectPromotion() {
    analytics?.trackSelectPromotion(toPromotionParams(activeItem, activeIndex))
  }

  function handleArrowClick(direction: 'next' | 'prev') {
    const fromIndex = activeIndex
    const toIndex = direction === 'next' ? activeIndex + 1 : activeIndex - 1
    analytics?.trackClick({
      click_type: 'carousel_arrow',
      click_subtype: NO_APLICA,
      link_text: NO_APLICA,
      link_url: NO_APLICA,
      link_domain: NO_APLICA,
      module_name: CAROUSEL_ANALYTICS_MODULE_NAME,
      module_type: CAROUSEL_ANALYTICS_MODULE_TYPE,
      nav_level: NO_APLICA,
      nav_path: NO_APLICA,
      action_detail: direction,
      action_value: String((((toIndex % items.length) + items.length) % items.length) + 1),
      action_context: String(fromIndex + 1),
    })
    goTo(toIndex)
  }

  return (
    <section
      ref={sectionRef}
      className={styles.hero}
      aria-label={SECTION_LABEL}
      aria-roledescription="carousel"
    >
      {shouldRenderScript && (
        <Script src="https://www.youtube.com/iframe_api" strategy="afterInteractive" />
      )}

      {/* Fondo panorámico — bajo los dos slots de vídeo; actúa como fallback
          mientras el reproductor de la slide activa se inicializa o no está
          listo (FR-009, FR-021). Se renderizan TODAS las imágenes apiladas
          (paridad con la referencia) y se alternan por opacidad — nunca se
          remontan, así el cross-fade entre slides es una transición CSS real
          en vez de un corte. */}
      <div className={styles.background} aria-hidden="true">
        {items.map((item, index) => (
          // eslint-disable-next-line @next/next/no-img-element -- imagen remota del CMS, no conocida en build time
          <img
            key={item.id}
            src={item.panoramicImage}
            alt=""
            loading={index === activeIndex ? 'eager' : 'lazy'}
            className={styles.backgroundImage}
            data-active={mounted && index === activeIndex ? '' : undefined}
          />
        ))}
      </div>

      {/* Dos contenedores físicos y estables: el player de YouTube nunca se
          mueve entre ellos, solo se alterna cuál está visible (research.md §5). */}
      <div
        className={styles.videoSlot}
        data-visible={visibleSlot === 'A' && isVideoPlaying ? '' : undefined}
        ref={slotARef}
        aria-hidden="true"
      />
      <div
        className={styles.videoSlot}
        data-visible={visibleSlot === 'B' && isVideoPlaying ? '' : undefined}
        ref={slotBRef}
        aria-hidden="true"
      />

      <div className={styles.overlay} aria-hidden="true" />

      <div className={styles.fg}>
        <div className={styles.content} key={activeItem.id}>
          {activeItem.highlighted ? (
            <p className={styles.highlightedLabel}>{HIGHLIGHTED_LABEL}</p>
          ) : (
            activeItem.category && <p className={styles.category}>{activeItem.category}</p>
          )}

          <h2 className={styles.title}>{activeItem.title}</h2>

          {activeItem.body && (
            <div className={styles.body} dangerouslySetInnerHTML={{ __html: activeItem.body }} />
          )}

          {priceText && (
            <p className={styles.price}>
              <span className={styles.priceLabel}>{PRICE_FROM_PREFIX}</span>
              <span className={styles.priceAmount}>{priceText}</span>
            </p>
          )}

          {activeItem.link && (
            <Button
              href={activeItem.link}
              external={activeItem.target}
              variant="filled-inverse"
              size="m"
              className={styles.cta}
              onClick={handleSelectPromotion}
            >
              {CTA_LABEL}
            </Button>
          )}
        </div>

        {items.length > 1 && (
          <div className={styles.footer}>
            <div className={styles.navButtons}>
              <button
                type="button"
                onClick={() => handleArrowClick('prev')}
                aria-label="Evento anterior"
                className={styles.navButton}
              >
                <IconChevLeft size={18} />
              </button>
              <button
                type="button"
                onClick={togglePause}
                aria-label={
                  isPaused
                    ? `Reanudar ${hasVideoSlide ? 'vídeo y avance' : 'avance'} automático`
                    : `Pausar ${hasVideoSlide ? 'vídeo y avance' : 'avance'} automático`
                }
                aria-pressed={isPaused}
                className={styles.navButton}
              >
                {isPaused ? (
                  <span className={styles.playIcon}>
                    <IconPlay size={16} />
                  </span>
                ) : (
                  <IconPause size={16} />
                )}
              </button>
              <button
                type="button"
                onClick={() => handleArrowClick('next')}
                aria-label="Evento siguiente"
                className={styles.navButton}
              >
                <IconChevRight size={18} />
              </button>
            </div>

            {/* Carátulas de los próximos eventos — la activa primero,
                envolviendo cíclicamente (paridad visual con la referencia). */}
            <div ref={railRef} className={styles.rail} role="group" aria-label="Próximos eventos">
              {Array.from({ length: railCount }, (_, offset) => {
                const index = (activeIndex + offset) % items.length
                const item = items[index]
                if (!item) return null
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => goTo(index)}
                    aria-label={item.title || `Ir al evento ${index + 1}`}
                    className={styles.railCard}
                    data-active={offset === 0 ? '' : undefined}
                  >
                    {/* eslint-disable-next-line @next/next/no-img-element -- imagen remota del CMS */}
                    <img
                      src={item.verticalImage ?? item.panoramicImage}
                      alt=""
                      // Sin arrastre nativo de imagen: en algunos navegadores
                      // (Safari) arranca antes que el gesto de `useRailSwipe`
                      // y cancela el puntero (`pointercancel`).
                      draggable={false}
                      className={styles.railCardImg}
                    />
                  </button>
                )
              })}
            </div>

            <div className={styles.pageControl} aria-hidden="true">
              {items.map((item, index) => (
                <span
                  key={item.id}
                  className={styles.segment}
                  data-active={index === activeIndex ? '' : undefined}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  )
}
